import { External } from '../external';

const BASE_URL = `https://${External.serviceApiDomain}/comment/admin`;

const getPaginationQueryParams = (timestamp, limit, order) => {
  const searchParams = new URLSearchParams();

  if (timestamp) {
    searchParams.append('timestamp', timestamp);
  }
  if (limit) {
    searchParams.append('limit', limit);
  }
  if (order) {
    searchParams.append('order', order);
  }

  return searchParams.toString();
};

const CommentAdminService = {
  getCurrentAuthor: () => {
    return {
      memberId: -1 * External.userId,
      name: External.userName,
    };
  },
  isCommentBelongToAdmin: (comment) => {
    const currentAuthor = CommentAdminService.getCurrentAuthor();
    return currentAuthor.memberId === comment.author.memberId;
  },
  getAllComments: async (timestamp, limit, order) => {
    const url = `${BASE_URL}?${getPaginationQueryParams(timestamp, limit, order)}`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
    return await response.json();
  },
  getAllRootCommentsByResource: async (resourceType, resourceId, timestamp, limit, order) => {
    const url = `${BASE_URL}/${resourceType}/${resourceId}?${getPaginationQueryParams(timestamp, limit, order)}`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
    return await response.json();
  },
  getAllRootCommentsByOnlyResourceType: async (resourceType, timestamp, limit, order) => {
    const url = `${BASE_URL}/${resourceType}?${getPaginationQueryParams(timestamp, limit, order)}`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
    return await response.json();
  },
  getAllCommentsByParent: async (parentId, timestamp, limit, order) => {
    const url = `${BASE_URL}/p/${parentId}?${getPaginationQueryParams(timestamp, limit, order)}`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
    return await response.json();
  },
  getUnapprovedComments: async (timestamp, limit, order) => {
    const url = `${BASE_URL}/waiting-approval?${getPaginationQueryParams(timestamp, limit, order)}`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
    return await response.json();
  },
  getUnapprovedCommentsByResource: async (resourceType, timestamp, limit, order) => {
    const url = `${BASE_URL}/${resourceType}/waiting-approval?${getPaginationQueryParams(timestamp, limit, order)}`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
    return await response.json();
  },
  getUnapprovedCommentsBySpecificResource: async (resourceType, resourceId, timestamp, limit, order) => {
    const url = `${BASE_URL}/${resourceType}/${resourceId}/waiting-approval?${getPaginationQueryParams(timestamp, limit, order)}`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
    return await response.json();
  },
  createCommentByParent: async (parentId, content, attachments) => {
    const url = `${BASE_URL}/${parentId}`;
    const response = await fetch(url, {
      method: 'POST',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      },
      body: JSON.stringify({
        content,
        attachments
      })
    });
    return await response.json();
  },
  updateComment: async (commentId, content, attachments) => {
    const url = `${BASE_URL}/${commentId}`;
    const response = await fetch(url, {
      method: 'PATCH',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      },
      body: JSON.stringify({
        content: content,
        attachments: attachments
      })
    });

    return await response.json();
  },
  deleteComment: async (commentId) => {
    const url = `${BASE_URL}/${commentId}`;
    const response = await fetch(url, {
      method: 'DELETE',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });

    return response.ok;
  },
  approveComment: async (commentId) => {
    const url = `${BASE_URL}/${commentId}/approve`;
    const response = await fetch(url, {
      method: 'PATCH',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });

    return response.ok;
  },
  giveReaction: async (commentId, reaction) => {
    const url = `${BASE_URL}/${commentId}/react/${reaction}`;
    await fetch(url, {
      method: 'POST',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
  },
  removeReaction: async (commentId) => {
    const url = `${BASE_URL}/${commentId}/react`;
    await fetch(url, {
      method: 'DELETE',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
  },
  getContexts: async (resourceType) => {
    const url = `${BASE_URL}/context?resourceType=${resourceType}`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
    return await response.json();
  },
  deleteContext: async (resourceType, resourceId) => {
    const url = `${BASE_URL}/context/${resourceType}/${resourceId}`;
    await fetch(url, {
      method: 'DELETE',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
  },
  uploadImageAndReturnDetails: async (image) => {
    const CLIENT_UPLOADS_FOLDER = '__client_uploads__';
    return new Promise((resolve, reject) => {
      if (!image) {
        reject('Image is null!');
      }
      if (image.base) {
        reject('Already uploaded! image: ' + JSON.stringify(image));
      }
      External.WebPlatform.makeClientWebsiteRequest({
        action: 'presignedUrl',
        data: {
          uploadingFileType: 'image',
          folder: CLIENT_UPLOADS_FOLDER,
          filename: image.name,
          fileType: image.type
        },
        onOk: async function (response) {
          if (response.ok) {
            if (response.data === 'NO_STORAGE_AVAILABLE') {
              reject('NO_STORAGE_AVAILABLE');
            } else {
              await fetch(response.data.url, {
                method: 'PUT',
                headers: {
                  'Content-Type': image.type
                },
                body: image
              });
              resolve({
                base: response.data.base,
                name: `${CLIENT_UPLOADS_FOLDER}/${response.data.fileName}`
              });
            }
          } else {
            reject('Response of presignedUrl was not ok: ' + response.message);
          }
        },
        onNotOk: function (response) {
          reject('Error while trying get presignedUrl: ' + response.message);
        }
      });
    });
  },
  getAdminAuthor: async () => {
    const url = `${BASE_URL}/admin-author`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
    return await response.json();
  },
  updateAdminAuthor: async (name, extra) => {
    const url = `${BASE_URL}/admin-author`;
    const response = await fetch(url, {
      method: 'PATCH',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      },
      body: JSON.stringify({
        name: name,
        extra: extra
      })
    });

    return await response.json();
  },
  uploadAdminAvatarImageAndReturnDetails: async (image) => {
    const CLIENT_UPLOADS_FOLDER = '__client_uploads__';
    return new Promise((resolve, reject) => {
      if (!image) {
        reject('Image is null!');
      }
      if (image.base) {
        reject('Already uploaded! image: ' + JSON.stringify(image));
      }
      WebPlatform.makeClientWebsiteRequest({
        action: 'presignedUrl',
        data: {
          uploadingFileType: 'image',
          folder: CLIENT_UPLOADS_FOLDER,
          filename: `avatar-admin-${image.name}`,
          fileType: image.type
        },
        onOk: async function (response) {
          if (response.ok) {
            if (response.data === 'NO_STORAGE_AVAILABLE') {
              reject('NO_STORAGE_AVAILABLE');
            } else {
              await fetch(response.data.url, {
                method: 'PUT',
                headers: {
                  'Content-Type': image.type
                },
                body: image
              });
              resolve({
                base: response.data.base,
                name: `${CLIENT_UPLOADS_FOLDER}/${response.data.fileName}`
              });
            }
          } else {
            reject('Response of presignedUrl was not ok: ' + response.message);
          }
        },
        onNotOk: function (response) {
          reject('Error while trying get presignedUrl: ' + response.message);
        }
      });
    });
  }
};

export default CommentAdminService;
